import { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  App,
  Card,
  Empty,
  Button,
  Space,
  Form,
  Input,
  Select,
  Typography,
  Grid,
  Spin,
  Table,
  Tooltip,
  Pagination,
} from 'antd'
import {
  PlusCircleOutlined,
  SearchOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  fetchScheduledTasks,
  fetchScheduledTaskStats,
  cancelScheduledTask,
} from '../store/slices/scheduledTaskSlice'
import { formatDate, formatDateTime, formatCurrency, parseNyToLocal } from '../utils/helpers'
import './ScheduledTasks.css'
import '../styles/mobile-record.css'
import './TaskHistory.css'

const { Option } = Select
const { Text } = Typography
const { useBreakpoint } = Grid

// Format next execution time in user's local timezone
const formatNextExecutionTime = (dateTime) => {
  const localTime = parseNyToLocal(dateTime)
  if (!localTime) return '—'
  return localTime.format('YYYY-MM-DD HH:mm')
}

const STATUS_STYLES = {
  ACTIVE: { color: '#0f766e', backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', label: 'Active' },
  CANCELED: { color: '#6b7280', backgroundColor: '#f3f4f6', borderColor: '#d1d5db', label: 'Canceled' },
  PAYMENT_ISSUE: { color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca', label: 'Payment Issue' },
  DEFAULT: { color: '#475569', backgroundColor: '#f8fafc', borderColor: '#e2e8f0', label: 'Unknown' },
}

const DEPTH_STYLES = {
  1: { label: 'Shallow', style: { color: '#4338ca', backgroundColor: '#eef2ff', borderColor: '#c7d2fe' } },
  2: { label: 'Medium', style: { color: '#7c3aed', backgroundColor: '#f3e8ff', borderColor: '#e9d5ff' } },
  3: { label: 'Deep', style: { color: '#0369a1', backgroundColor: '#e0f2fe', borderColor: '#bae6fd' } },
  4: { label: 'Very Deep', style: { color: '#0f766e', backgroundColor: '#d1fae5', borderColor: '#a7f3d0' } },
  5: { label: 'Maximum', style: { color: '#92400e', backgroundColor: '#fef3c7', borderColor: '#fde68a' } },
  default: { label: 'Depth', style: { color: '#1f2937', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' } },
}

const ANALYST_TAG_STYLE = { color: '#0f172a', backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }

const ANALYST_LABELS = {
  market: 'Market Analyst',
  social: 'Social Sentiment',
  news: 'News Analysis',
  fundamentals: 'Fundamental Analysis',
}

const ScheduledTasks = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { modal, message } = App.useApp()
  const { tasks, loading, pagination, stats } = useSelector((state) => state.scheduledTask)
  const [form] = Form.useForm()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [queryParams, setQueryParams] = useState({
    page: 0,
    pageSize: 12,
    status: undefined,
    ticker: undefined,
  })
  const [filtersOpen, setFiltersOpen] = useState(!isMobile)

  useEffect(() => {
    loadTasks()
    loadStats()
  }, [])

  useEffect(() => {
    setFiltersOpen(!isMobile)
  }, [isMobile])

  const loadTasks = useCallback((params = queryParams) => {
    dispatch(fetchScheduledTasks(params))
    setQueryParams(params)
  }, [dispatch, queryParams])

  const loadStats = useCallback(() => {
    dispatch(fetchScheduledTaskStats())
  }, [dispatch])

  const handleSearch = () => {
    const values = form.getFieldsValue()
    const newParams = {
      ...queryParams,
      page: 0,
      status: values.status === 'all' ? undefined : values.status,
      ticker: values.ticker || undefined,
    }
    loadTasks(newParams)
    if (isMobile) {
      setFiltersOpen(false)
    }
  }

  const handleCreateSubscription = () => {
    const ticker = form.getFieldValue('ticker')?.trim().toUpperCase()
    const params = new URLSearchParams()
    if (ticker) {
      params.set('ticker', ticker)
    }
    const queryString = params.toString()
    navigate(queryString ? `/scheduled-tasks/new?${queryString}` : '/scheduled-tasks/new')
  }

  const handlePageChange = (page, pageSize) => {
    const newParams = {
      ...queryParams,
      page: page - 1,
      pageSize: pageSize,
    }
    loadTasks(newParams)
  }

  const confirmUnsubscribe = useCallback((taskId) => {
    modal.confirm({
      title: 'Unsubscribe from this analysis?',
      icon: <ExclamationCircleFilled />,
      content: 'This will cancel the subscription now and refund the paid executions that have not run yet.',
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        danger: true,
      },
      centered: true,
      footer: (_, { OkBtn, CancelBtn }) => (
        <div className="scheduled-tasks-unsubscribe-modal__footer">
          <CancelBtn />
          <OkBtn />
        </div>
      ),
      onOk: async () => {
        try {
          const response = await dispatch(cancelScheduledTask(taskId)).unwrap()
          const refundAmount = Number(response?.refundAmount ?? 0)
          const refundCurrency = response?.refundCurrency || 'USD'
          if (Number.isFinite(refundAmount) && refundAmount > 0) {
            message.success(
              `Subscription canceled. Refund initiated for ${formatCurrency(refundAmount, refundCurrency)}.`,
            )
          } else {
            message.success('Subscription canceled successfully.')
          }
        } catch (error) {
          const errorMessage = typeof error === 'string' ? error : error?.message
          message.error(errorMessage || 'Failed to cancel subscription')
        } finally {
          loadTasks()
          loadStats()
        }
      },
    })
  }, [modal, message, dispatch, loadTasks, loadStats])

  const renderSoftBadge = useCallback((label, palette) => (
    <span className="task-history-soft-tag" style={palette}>
      {label}
    </span>
  ), [])

  // Desktop table columns
  const desktopColumns = useMemo(() => {
    const columns = [
      {
        title: 'Stock',
        key: 'stock',
        align: 'left',
        render: (_, task) => (
          <Space direction="vertical" size={0} className="task-history-stock">
            <Text className="task-history-stock__ticker" strong>
              {task.ticker || '-'}
            </Text>
            <Text type="secondary" className="task-history-stock__date" style={{ fontSize: 12 }}>
              {task.createdAt ? formatDateTime(task.createdAt) : '—'}
            </Text>
          </Space>
        ),
        width: 150,
      },
      {
        title: 'Depth',
        key: 'depth',
        align: 'center',
        render: (_, task) => {
          const depthLevel = Number(task.researchDepth ?? 1)
          const depthStyle = DEPTH_STYLES[depthLevel] || DEPTH_STYLES.default
          return renderSoftBadge(depthStyle.label, depthStyle.style)
        },
        width: 150,
      },
      {
        title: 'Analysts',
        key: 'analysts',
        align: 'center',
        render: (_, task) => {
          const selected = task.selectedAnalysts
          const analystNames = Array.isArray(selected) ? selected.filter(Boolean) : []
          const fallbackCount = Number(task.analystCount ?? 0)
          const analystCount = analystNames.length || fallbackCount

          if (!analystCount) {
            return <Text type="secondary">-</Text>
          }

          const label = `${analystCount} ${analystCount === 1 ? 'Analyst' : 'Analysts'}`
          const content = renderSoftBadge(label, ANALYST_TAG_STYLE)

          return analystNames.length > 0 ? (
            <Tooltip title={analystNames.map(a => ANALYST_LABELS[a] || a).join(', ')}>
              {content}
            </Tooltip>
          ) : content
        },
        width: 160,
      },
      {
        title: 'Status',
        key: 'status',
        align: 'center',
        render: (_, task) => {
          const execStatus = (task.status || '').toUpperCase()
          const subStatus = (task.subscriptionStatus || '').toUpperCase()
          // Priority: Payment Issue > Canceled > subscription status
          if (execStatus === 'PAYMENT_ISSUE') {
            return renderSoftBadge('Payment Issue', STATUS_STYLES.PAYMENT_ISSUE)
          }
          if (subStatus === 'CANCELED') {
            return renderSoftBadge('Canceled', STATUS_STYLES.CANCELED)
          }
          const style = STATUS_STYLES[subStatus] || STATUS_STYLES.DEFAULT
          return renderSoftBadge(style.label, style)
        },
        width: 160,
      },
      {
        title: 'Executions',
        key: 'executions',
        align: 'center',
        render: (_, task) => {
          const periodExecutions = task.periodExecutions ?? 0
          const periodTaskCount = task.periodTaskCount ?? 0
          const successfulExecutions = task.successfulExecutions ?? 0
          const totalExecutions = task.totalExecutions ?? 0

          return (
            <Text>Total: {totalExecutions}</Text>
          )
        },
        width: 160,
      },
      {
        title: 'Billing',
        key: 'billing',
        align: 'right',
        render: (_, task) => {
          const periodBilling = formatCurrency(task.billingAmount, task.billingCurrency)
          const totalPaid = formatCurrency(task.totalPaid, task.billingCurrency)

          return (
            <Text strong>
              {periodBilling ? `${periodBilling} / mo` : '—'}
            </Text>
          )
        },
        width: 200,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_, task) => {
          const subscriptionStatus = (task.subscriptionStatus || '').toUpperCase()

          if (subscriptionStatus === 'ACTIVE') {
            return (
              <Tooltip title="Unsubscribe">
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  style={{ color: '#ef4444' }}
                  onClick={(event) => {
                    event.stopPropagation()
                    confirmUnsubscribe(task.id)
                  }}
                />
              </Tooltip>
            )
          }

          return null
        },
        width: 80,
      },
    ]

    const getHeaderClass = (alignValue) => {
      if (alignValue === 'right') return 'task-history-header-right'
      if (alignValue === 'center') return 'task-history-header-center'
      return 'task-history-header-left'
    }

    return columns.map((column) => {
      const alignValue = column.align || 'left'
      return {
        ...column,
        onHeaderCell: () => ({ className: getHeaderClass(alignValue) }),
      }
    })
  }, [renderSoftBadge, confirmUnsubscribe])

  const tasksToRender = tasks || []
  const currentPage = (pagination?.currentPage ?? 0) + 1
  const pageSize = pagination?.pageSize ?? queryParams.pageSize
  const totalItems = pagination?.totalElements ?? tasksToRender.length
  const hasTasks = tasksToRender.length > 0

  return (
    <div className={`task-history-page ${isMobile ? 'mobile-record-page' : ''}`}>
      {isMobile && (
        <div className="task-history-filter-toggle mobile-record-toolbar">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="task-history-toggle-button mobile-record-toggle-btn"
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>
      )}

      {(!isMobile || filtersOpen) && (
        <Card
          bordered={false}
          className="task-history-filter-card mobile-record-filter-card"
          styles={{ body: { padding: isMobile ? 16 : 24 } }}
        >
          {isMobile && (
            <div className="task-history-filter-mobile-header">
              <Text className="task-history-filter-mobile-title">Filters</Text>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setFiltersOpen(false)}
                className="task-history-filter-close"
              />
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            className="task-history-filter-form mobile-record-filter-form"
            onFinish={handleSearch}
            initialValues={{
              status: 'all',
            }}
          >
            <div className="task-history-filter-grid mobile-record-filter-grid">
              <Form.Item name="ticker" label={null} colon={false}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search by ticker"
                  allowClear
                  size="large"
                />
              </Form.Item>

              <Form.Item name="status" label={null} colon={false}>
                <Select placeholder="Select status" size="large" allowClear>
                  <Option value="all">All Status</Option>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="CANCELED">Canceled</Option>
                  <Option value="PAYMENT_ISSUE">Payment Issue</Option>
                </Select>
              </Form.Item>

              <div className="task-history-filter-actions mobile-record-filter-actions">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  className="task-history-search-btn mobile-record-search-btn"
                >
                  Search
                </Button>
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={handleCreateSubscription}
                  size="large"
                  className="task-history-create-btn mobile-record-create-btn"
                >
                  Create Subscription
                </Button>
              </div>
            </div>
          </Form>

          {/* Stats - inline with filters */}
          {!isMobile && stats && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
              <Space size={24} split={<span style={{ borderLeft: '1px solid #e2e8f0' }} />}>
                <Space size={8}>
                  <Text type="secondary">Total:</Text>
                  <Text strong>{stats.total || 0}</Text>
                </Space>
                <Space size={8}>
                  <Text type="secondary">Active:</Text>
                  <Text strong style={{ color: '#10b981' }}>{stats.active || 0}</Text>
                </Space>
                {(stats.canceled > 0) && (
                  <Space size={8}>
                    <Text type="secondary">Canceled:</Text>
                    <Text strong style={{ color: '#6b7280' }}>
                      {stats.canceled || 0}
                    </Text>
                  </Space>
                )}
                {(stats.paymentFailed > 0) && (
                  <Space size={8}>
                    <Text type="secondary">Issues:</Text>
                    <Text strong style={{ color: '#ef4444' }}>
                      {stats.paymentFailed || 0}
                    </Text>
                  </Space>
                )}
              </Space>
            </div>
          )}
        </Card>
      )}

      {/* Task Records */}
      {!hasTasks ? (
        <Card className="task-history-empty-card">
          {loading ? (
            <div className="task-history-empty-loading">
              <Spin size="large" />
            </div>
          ) : (
            <Empty
              description={
                <Space direction="vertical" size={12}>
                  <span>No scheduled tasks found</span>
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={handleCreateSubscription}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    Create Your First Scheduled Task
                  </Button>
                </Space>
              }
            />
          )}
        </Card>
      ) : (
        <>
          {!isMobile ? (
            <Card
              bordered={false}
              className="task-history-table-card"
              styles={{ body: { padding: 0 } }}
            >
              <Table
                columns={desktopColumns}
                dataSource={tasksToRender}
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalItems,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['12', '24', '48', '96'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} subscriptions`,
                }}
                onChange={(tablePagination) => {
                  handlePageChange(tablePagination.current, tablePagination.pageSize)
                }}
                // TODO: Detail page navigation temporarily disabled
                // onRow={(record) => ({
                //   onClick: (event) => {
                //     const target = event?.target
                //     const shouldIgnore = target?.closest?.('button, a, .ant-btn')
                //     if (shouldIgnore) return
                //     navigate(`/scheduled-tasks/${record.id}`)
                //   },
                // })}
                rowKey={(task) => task.id}
                // rowClassName="scheduled-task-row-clickable"
                scroll={{ x: true }}
                size="middle"
              />
            </Card>
          ) : (
            <>
              <Spin spinning={loading}>
                <div className="mobile-record-cards">
                  {tasksToRender.map((task) => (
                    <ScheduledTaskCard
                      key={task.id}
                      task={task}
                      onUnsubscribe={confirmUnsubscribe}
                      // onView={() => navigate(`/scheduled-tasks/${task.id}`)}
                    />
                  ))}
                </div>
              </Spin>

              {totalItems > 0 && (
                <Pagination
                  className="mobile-record-pagination"
                  current={currentPage}
                  total={totalItems}
                  pageSize={pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} subscriptions`}
                  pageSizeOptions={['12', '24', '48', '96']}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  size="small"
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

// Mobile Card Component
const ScheduledTaskCard = ({ task, onUnsubscribe }) => {
  const execStatus = (task.status || '').toUpperCase()
  const subscriptionStatus = (task.subscriptionStatus || '').toUpperCase()
  // Derive display status: Payment Issue > Canceled > subscription status
  const displayStatus = execStatus === 'PAYMENT_ISSUE' ? 'PAYMENT_ISSUE'
    : subscriptionStatus === 'CANCELED' ? 'CANCELED'
    : subscriptionStatus
  const style = STATUS_STYLES[displayStatus] || STATUS_STYLES.DEFAULT
  const canUnsubscribe = subscriptionStatus === 'ACTIVE'

  return (
    <Card className="mobile-record-card scheduled-task-mobile-card">
      <div className="mobile-record-card-header">
        <div className="mobile-record-card-title">
          <Text strong style={{ fontSize: 18 }}>
            {task.ticker}
          </Text>
        </div>
        <span className="task-history-soft-tag" style={style}>
          {style.label}
        </span>
      </div>

      <div className="mobile-record-card-body">
        <div className="mobile-record-card-row">
          <Text type="secondary" className="mobile-record-card-row-label">Depth</Text>
          {(() => {
            const depthLevel = Number(task.researchDepth ?? 1)
            const depthStyle = DEPTH_STYLES[depthLevel] || DEPTH_STYLES.default
            return (
              <span className="task-history-soft-tag mobile-record-card-row-value" style={depthStyle.style}>
                {depthStyle.label}
              </span>
            )
          })()}
        </div>

        <div className="mobile-record-card-row">
          <Text type="secondary" className="mobile-record-card-row-label">Analysts</Text>
          {(() => {
            const selected = task.selectedAnalysts
            const analystNames = Array.isArray(selected) ? selected.filter(Boolean) : []
            const fallbackCount = Number(task.analystCount ?? 0)
            const analystCount = analystNames.length || fallbackCount

            if (!analystCount) {
              return <Text type="secondary" className="mobile-record-card-row-value">-</Text>
            }

            const label = `${analystCount} ${analystCount === 1 ? 'Analyst' : 'Analysts'}`
            return (
              <Tooltip title={analystNames.map(a => ANALYST_LABELS[a] || a).join(', ')}>
                <span className="task-history-soft-tag mobile-record-card-row-value" style={ANALYST_TAG_STYLE}>{label}</span>
              </Tooltip>
            )
          })()}
        </div>


        <div className="mobile-record-card-row">
          <Text type="secondary" className="mobile-record-card-row-label">Total Exec</Text>
          <Text className="mobile-record-card-row-value">
            {task.totalExecutions ?? 0}
          </Text>
        </div>

        <div className="mobile-record-card-row">
          <Text type="secondary" className="mobile-record-card-row-label">Monthly Cost</Text>
          <Text strong className="mobile-record-card-row-value">
            {formatCurrency(task.billingAmount, task.billingCurrency) || '—'} / mo
          </Text>
        </div>
      </div>

      {canUnsubscribe && (
        <div
          className="mobile-record-card-actions scheduled-task-mobile-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <Text type="secondary" className="scheduled-task-mobile-actions-label">
            Action
          </Text>
          <Space size={8}>
            <Button
              type="default"
              icon={<CloseCircleOutlined />}
              className="scheduled-task-mobile-action-btn scheduled-task-mobile-action-btn--pause"
              onClick={() => onUnsubscribe(task.id)}
            >
              Unsubscribe
            </Button>
          </Space>
        </div>
      )}
    </Card>
  )
}

export default ScheduledTasks
